import React from 'react';
import ActionsMenu from '../../../../components/ActionsMenu';

/**
 * ActivityFeed component
 * Displays the latest changes activity feed with sorting controls
 */
export default function ActivityFeed({
    latestChanges,
    activitySortBy,
    activitySortOrder,
    onSortByChange,
    onSortOrderToggle,
    onViewChangeDetails,
    onRevertMapping
}) {
    return (
        <div style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            flex: 1
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid var(--border)',
                paddingBottom: '0.75rem'
            }}>
                <h3 style={{
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    margin: 0,
                    color: 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                }}>
                    <i className="fa-solid fa-history" style={{ color: 'var(--accent)' }}></i>
                    Latest Changes
                </h3>
                
                {/* Sorting Controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Sort:</span>
                    <select
                        value={activitySortBy}
                        onChange={(e) => onSortByChange(e.target.value)}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--text-primary)',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            outline: 'none',
                            padding: '2px 4px'
                        }}
                    >
                        <option value="name" style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>Name</option>
                        <option value="date" style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>Date Modified</option>
                        <option value="date_created" style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>Date Created</option>
                        <option value="user" style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>Created By</option>
                    </select>
                    <button
                        onClick={onSortOrderToggle}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)',
                            fontSize: '0.8rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '2px',
                            borderRadius: '4px',
                            transition: 'var(--transition)'
                        }}
                        title={activitySortOrder === 'asc' ? 'Sort Ascending' : 'Sort Descending'}
                    >
                        <i className={`fa-solid ${activitySortOrder === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                    </button>
                </div>
            </div>
            
            {latestChanges.length > 0 ? (
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingLeft: '1.25rem', marginLeft: '-1.25rem', paddingRight: '0.5rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', borderLeft: '2px solid var(--border)', paddingLeft: '1.25rem', marginLeft: '0.5rem' }}>
                        {latestChanges.map((log) => (
                            <ActivityItem
                                key={log.id}
                                log={log}
                                onClick={() => onViewChangeDetails(log)}
                                onRevertMapping={onRevertMapping}
                            />
                        ))}
                    </div>
                </div>
            ) : (
                <p style={{ fontStyle: 'italic', color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
                    No recent activity logged.
                </p>
            )}
        </div>
    );
}

/**
 * ActivityItem component
 * Individual activity item in the feed
 */
function ActivityItem({ log, onClick, onRevertMapping }) {
    return (
        <div
            onClick={onClick}
            className="table-row-hover"
            style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem',
                fontSize: '0.8rem',
                minHeight: '2.2rem',
                padding: '0.25rem 0.5rem',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                transition: 'var(--transition)'
            }}
        >
            {/* Dot marker */}
            <div style={{
                position: 'absolute',
                left: '-1.65rem',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: log.color,
                border: '2px solid var(--bg-card)'
            }}></div>
            
            {/* Activity name and Icon */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flex: '0 0 auto', minWidth: '100px' }}>
                <i className={log.icon} style={{ fontSize: '0.75rem', color: log.color }}></i>
                <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                    {log.activity}
                </span>
            </div>

            {/* Item / Subject name */}
            <div style={{ 
                flex: '1 1 auto', 
                minWidth: '60px', 
                fontFamily: 'monospace', 
                color: 'var(--text-secondary)', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap' 
            }}>
                {log.item}
            </div>

            {/* User email */}
            <div style={{ 
                flex: '0 1 auto', 
                minWidth: '100px', 
                color: 'var(--text-secondary)', 
                fontSize: '0.75rem', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                whiteSpace: 'nowrap' 
            }} title={log.user}>
                {log.user}
            </div>

            {/* Timestamp & Revert Button */}
            <div style={{ 
                flex: '0 0 auto', 
                minWidth: '160px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'flex-end', 
                gap: '0.5rem' 
            }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontStyle: 'italic' }}>
                    {new Date(log.date).toLocaleString()}
                </span>
                <ActionsMenu
                    label={`Actions for ${log.activity}`}
                    items={[
                        {
                            label: 'Revert Change',
                            icon: 'fa-solid fa-clock-rotate-left',
                            danger: true,
                            onSelect: () => onRevertMapping(log)
                        }
                    ]}
                />
            </div>
        </div>
    );
}
